import { LitElement, html, css, svg } from 'lit';
import { customElement } from 'lit/decorators.js';
import { createEditor } from './editor/editor';
import type { EditorView } from 'prosemirror-view';

import {
  manualToDocFromBlocks,
  editorStateToBlocks,
  normalizeContentAsPages,
  blocksFromSlice,
} from './editor/json-converter';
import { createPrintDocument } from './editor/print-utils';
import './editor/color-picker-button';
import type { ManualContent } from './editor/json-converter';
import {
  insertBlockXamix,
  insertInlineXamix,
  insertTable,
  getTableMargin,
  setTableMargin,
  getTableBackgroundColor,
  setTableBackgroundColor,
  getTableBorderColor,
  setTableBorderColor,
  setCellBackgroundColor,
  getCellBackgroundColor,
  insertHardBreak,
  insertInlineImage,
  insertInlineSvg,
  getTextColor,
  setTextColor,
  getHighlightColor,
  setHighlightColor,
  wrapBulletList,
  wrapOrderedList,
  addColumnAfterCmd,
  addColumnBeforeCmd,
  addRowAfterCmd,
  addRowBeforeCmd,
  removeColumnCmd,
  removeRowCmd,
  mergeCellsCmd,
  splitCellCmd,
} from './editor/commands';
import { toggleMark } from 'prosemirror-commands';
import { deleteTable, isInTable } from 'prosemirror-tables';
import Pickr from '@simonwep/pickr';

const SESSION_KEY = 'nawatl-manual-editor-state';

/** Preset colors for text (also used for table/cell) */
const TEXT_COLORS = [
  { value: '', label: 'Por defecto' },
  { value: '#000000', label: 'Negro' },
  { value: '#374151', label: 'Gris oscuro' },
  { value: '#2563eb', label: 'Azul' },
  { value: '#059669', label: 'Esmeralda' },
  { value: '#db2777', label: 'Rosa' },
  { value: '#7c3aed', label: 'Violeta' },
  { value: '#ea580c', label: 'Naranja' },
  { value: '#b91c1c', label: 'Rojo' },
];

/** Preset colors for table and cell background (bold, saturated) */
const TABLE_COLORS = [
  { value: '', label: 'Ninguno' },
  { value: '#374151', label: 'Gris oscuro' },
  { value: '#f59e0b', label: 'Ámbar' },
  { value: '#2563eb', label: 'Azul' },
  { value: '#059669', label: 'Esmeralda' },
  { value: '#db2777', label: 'Rosa' },
  { value: '#7c3aed', label: 'Violeta' },
  { value: '#ea580c', label: 'Naranja' },
];

/** Sample content for new manual */
const SAMPLE_CONTENT: ManualContent = {
  title: 'Ma titomachtikan totlatol (Xamix)',
  sections: [
    {
      title: 'Introducción',
      blocks: [
        { type: 'centered', content: 'MANUAL DE LENGUA NAWATL', size: 'large' },
        { type: 'centered', content: 'MA TITOMACHTIKAN TOTLATOL', size: 'large' },
        { type: 'centered', content: '(APRENDAMOS NUESTRA PALABRA)', size: 'normal' },
        { type: 'heading', content: 'Introducción', level: 2 },
        {
          type: 'text',
          content:
            'El manual que tienes en las manos desarrolla la comprensión lectora y auditiva, y la habilidad para hablar y escribir en lengua nawatl.',
        },
        { type: 'xamix', content: 'na-watl' },
        {
          type: 'text',
          content: ' se presenta en ortografía Xamix (sílabas con guiones).',
        },
      ],
    },
  ],
};

@customElement('nawatl-manual-editor')
export class NawatlManualEditor extends LitElement {
  static styles = css`
    :host {
      display: block;
      min-height: 100vh;
      background: #f5f5f5;
      font-family: 'Segoe UI', system-ui, sans-serif;
      color: #1a1a1a;
    }
    :host([data-theme="dark"]) {
      background: #1a1a1a;
      color: #e5e5e5;
    }

    .layout {
      display: flex;
      min-height: 100vh;
      background: #e8e8e8;
    }
    :host([data-theme="dark"]) .layout {
      background: #121212;
    }

    .page-panel {
      width: 180px;
      min-width: 180px;
      background: #fafafa;
      border-right: 1px solid #e0e0e0;
      padding: 1rem;
      overflow-y: auto;
    }

    :host([data-theme="dark"]) .page-panel {
      background: #1e1e1e;
      border-right-color: #333;
    }
    .page-panel h4 {
      margin: 0 0 0.75rem;
      font-size: 0.85rem;
      color: #666;
    }
    :host([data-theme="dark"]) .page-panel h4 {
      color: #999;
    }

    .page-list {
      list-style: none;
      margin: 0;
      padding: 0;
    }

    .page-list li {
      margin: 0.25rem 0;
      display: flex;
      align-items: center;
      gap: 0.25rem;
    }
    .page-color-btn {
      width: 1.25rem;
      height: 1.25rem;
      padding: 0;
      border: 1px solid rgba(0,0,0,0.2);
      border-radius: 4px;
      cursor: pointer;
      background: repeating-conic-gradient(#ccc 0% 25%, #fff 0% 50%) 50% / 4px 4px;
      flex-shrink: 0;
    }
    .page-color-btn[style*="background-color"] {
      background: var(--page-bg) !important;
    }

    .page-list md-filled-tonal-button,
    .page-list md-outlined-button {
      flex: 1;
      --md-filled-tonal-button-container-shape: 8px;
      --md-outlined-button-container-shape: 8px;
    }

    .page-list md-icon-button {
      flex-shrink: 0;
    }

    .app {
      flex: 1;
      max-width: 800px;
      margin: 0 auto;
      padding: 1rem;
      background: #fff;
      min-height: 100vh;
      box-shadow: 0 0 20px rgba(0, 0, 0, 0.06);
      overflow-y: auto;
    }
    :host([data-theme="dark"]) .app {
      background: #1e1e1e;
      box-shadow: 0 0 20px rgba(0, 0, 0, 0.3);
    }

    .toolbar {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: 0.25rem;
      padding: 0.75rem 0;
      border-bottom: 1px solid #e0e0e0;
      margin-bottom: 1rem;
      background: #fafafa;
    }
    :host([data-theme="dark"]) .toolbar {
      border-bottom-color: #333;
      background: #2d2d2d;
    }
    :host([data-theme="dark"]) .toolbar md-icon-button,
    :host([data-theme="dark"]) .toolbar md-filled-icon-button,
    :host([data-theme="dark"]) .toolbar md-outlined-icon-button {
      --md-icon-button-icon-color: rgba(255, 255, 255, 0.87);
      --md-icon-button-hover-icon-color: rgba(255, 255, 255, 0.87);
      --md-icon-button-focus-icon-color: rgba(255, 255, 255, 0.87);
      --md-icon-button-pressed-icon-color: rgba(255, 255, 255, 0.87);
      --md-icon-button-selected-icon-color: rgba(255, 255, 255, 0.87);
      --md-icon-button-selected-container-color: rgba(255, 255, 255, 0.12);
    }
    :host([data-theme="dark"]) .toolbar md-outlined-icon-button[selected],
    :host([data-theme="dark"]) .toolbar md-filled-icon-button[selected] {
      --md-icon-button-container-color: rgba(255, 255, 255, 0.12);
    }
    :host([data-theme="dark"]) .toolbar md-icon {
      color: rgba(255, 255, 255, 0.87);
    }
    :host([data-theme="dark"]) .toolbar .toolbar-margin-label,
    :host([data-theme="dark"]) .toolbar label {
      color: rgba(255, 255, 255, 0.7);
    }
    :host([data-theme="dark"]) .toolbar .toolbar-margin-select {
      background: #404040;
      border-color: #555;
      color: rgba(255, 255, 255, 0.87);
    }
    :host([data-theme="dark"]) .toolbar-sep {
      background: #555;
    }

    .toolbar-sep {
      width: 1px;
      height: 20px;
      background: #ccc;
      margin: 0 0.25rem;
    }

    .editor-container {
      min-height: 400px;
      padding: 1rem;
      display: flex;
      flex-direction: column;
    }

    .editor-container .editor-content {
      outline: none;
      flex: 1;
      min-height: 360px;
      padding: 1rem;
      border: 2px dashed #666;
      border-radius: 4px;
      transition: border-color 0.15s ease;
      background: #fff;
    }
    :host([data-theme="dark"]) .editor-container .editor-content {
      border-color: #555;
      background: #1e1e1e;
      color: #e5e5e5;
    }

    .editor-container:focus-within .editor-content {
      border-color: transparent;
    }

    .editor-container :global(.manual-doc) {
      font-size: 1rem;
      line-height: 1.6;
    }

    .editor-container :global(.manual-doc p) {
      margin: 0 0 0.75em;
    }

    .editor-container :global(.manual-doc h1),
    .editor-container :global(.manual-doc h2),
    .editor-container :global(.manual-doc h3) {
      margin: 1em 0 0.5em;
      font-weight: 600;
    }

    .editor-container :global(.manual-doc [data-centered]) {
      text-align: center;
    }

    .editor-container :global(.manual-doc .centered-small) {
      font-size: 0.9em;
    }

    .editor-container :global(.manual-doc .centered-large) {
      font-size: 1.4em;
    }

    .editor-container :global(.manual-doc .block-xamix) {
      margin: 0.5em 0;
    }
    .editor-container :global(.manual-doc .block-xamix-h1) {
      margin: 1em 0 0.5em;
      font-weight: 600;
    }
    .editor-container :global(.manual-doc .block-xamix-h2) {
      margin: 0.8em 0 0.4em;
      font-weight: 600;
    }
    .editor-container :global(.manual-doc .block-xamix-h3) {
      margin: 0.6em 0 0.3em;
      font-weight: 600;
    }

    .editor-container :global(.manual-doc ul),
    .editor-container :global(.manual-doc ol) {
      margin: 0.5em 0;
      padding-left: 1.5em;
    }

    .editor-container :global(.manual-doc li) {
      margin: 0.25em 0;
    }
    .editor-container :global(.manual-doc mark) {
      padding: 0 0.1em;
      border-radius: 2px;
    }

    .editor-container :global(.manual-doc [data-page-break]) {
      height: 0;
      margin: 2em 0;
      border-top: 2px dashed #ccc;
      page-break-after: always;
    }
    .editor-container :global(.manual-doc [data-page-break])::after {
      content: '';
      display: block;
      min-height: 70vh;
    }

    .editor-container :global(.tableWrapper) {
      overflow-x: auto;
      margin: 0.75em 0;
    }
    .editor-container :global(.tableWrapper table) {
      table-layout: fixed;
      width: 100%;
      font-size: 0.95em;
      border-radius: 8px;
      overflow: hidden;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
    }
    /* The colgroup with column widths handles the sizing */
    .editor-container :global(.manual-table) {
      table-layout: fixed;
      width: 100%;
    }
    .editor-container :global(.tableWrapper td):not(:global([data-colwidth])):not(:global(.column-resize-dragging)),
    .editor-container :global(.tableWrapper th):not(:global([data-colwidth])):not(:global(.column-resize-dragging)) {
      min-width: var(--default-cell-min-width, 80px);
    }
    .editor-container :global(.manual-doc .manual-table) {
      width: 100%;
      margin: 0;
      font-size: 0.95em;
      border-radius: 8px;
      overflow: hidden;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
    }
    .editor-container :global(.column-resize-handle) {
      position: absolute;
      right: -2px;
      top: 0;
      bottom: 0;
      width: 6px;
      z-index: 20;
      background-color: var(--md-sys-color-primary, #6750a4);
      pointer-events: none;
      opacity: 0.6;
    }
    .editor-container :global(.resize-cursor) {
      cursor: col-resize;
      cursor: ew-resize;
    }
    .editor-container :global(.manual-doc .manual-table tbody tr:first-child td),
    .editor-container :global(.manual-doc .manual-table thead th),
    .editor-container :global(.manual-doc .manual-table thead td) {
      background: var(--md-sys-color-surface-container-highest, #e6e0e9);
      font-weight: 600;
    }
    .editor-container :global(.manual-doc .manual-table td),
    .editor-container :global(.manual-doc .manual-table th) {
      border: 1px solid #cac4d0 !important;
      padding: 0.5em 0.65em;
      vertical-align: top;
      min-width: 4em;
      box-sizing: border-box;
    }
    .editor-container :global(.manual-doc .manual-table[data-table-border-color] td),
    .editor-container :global(.manual-doc .manual-table[data-table-border-color] th) {
      border-color: var(--table-border-color) !important;
    }
    .editor-container :global(.manual-doc tbody tr:not(:first-child):nth-child(even) td) {
      background: var(--md-sys-color-surface-container-low, #f5f0f7);
    }
    .editor-container :global(.manual-doc .selectedCell) {
      background: rgba(100, 150, 255, 0.25) !important;
    }

    .editor-container :global(xamix-element) {
      vertical-align: middle;
      direction: rtl;
      unicode-bidi: embed;
      --xamix-horizontal-direction: rtl;
    }

    .editor-container :global(.inline-svg-wrapper) {
      display: inline-block;
      vertical-align: middle;
      min-width: 20px;
      min-height: 20px;
    }
    .editor-container :global(.inline-svg-wrapper svg) {
      display: block;
      max-width: 100%;
    }
    .editor-container :global(.inline-svg-popover) {
      position: absolute;
      bottom: 100%;
      left: 50%;
      transform: translateX(-50%);
      background: var(--md-sys-color-surface, #fff);
      border: 1px solid var(--md-sys-color-outline, #ccc);
      border-radius: 8px;
      padding: 8px 12px;
      display: flex;
      gap: 8px;
      align-items: center;
      z-index: 100;
      box-shadow: 0 2px 8px rgba(0,0,0,0.15);
      white-space: nowrap;
      margin-bottom: 4px;
    }

    .editor-container :global(.block-xamix) {
      direction: rtl;
      text-align: right;
    }

    @media print {
      .layout { display: block; }
      .page-panel { display: none; }
      .app { box-shadow: none; max-width: 100%; }
      .toolbar { display: none; }
    }

    .actions {
      margin-top: 1rem;
      display: flex;
      gap: 0.5rem;
      flex-wrap: wrap;
    }

    .file-input {
      display: none;
    }

    .xamix-dialog-backdrop {
      position: fixed;
      inset: 0;
      background: rgba(0, 0, 0, 0.5);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 1000;
    }

    .xamix-dialog {
      background: var(--md-sys-color-surface, #fff);
      padding: 1.5rem;
      border-radius: 28px;
      min-width: 300px;
      box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
    }

    .xamix-dialog-title {
      margin: 0 0 0.5rem;
      font-size: 1.25rem;
    }

    .xamix-dialog-hint,
    .xamix-dialog-label {
      color: var(--md-sys-color-on-surface-variant, #666);
      font-size: 0.9em;
      margin: 0 0 0.5rem;
    }

    .xamix-dialog-levels {
      display: flex;
      gap: 0.5rem;
      margin-bottom: 1rem;
      flex-wrap: wrap;
    }

    .xamix-dialog-checkbox {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      margin-bottom: 1rem;
      cursor: pointer;
    }

    .xamix-dialog-input {
      width: 100%;
      margin-bottom: 1rem;
      display: block;
    }

    .xamix-dialog-actions {
      display: flex;
      gap: 0.5rem;
      justify-content: flex-end;
    }

    .table-dialog-backdrop {
      position: fixed;
      inset: 0;
      background: rgba(0, 0, 0, 0.5);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 1000;
    }
    .table-dialog {
      background: var(--md-sys-color-surface, #fff);
      padding: 1.5rem;
      border-radius: 28px;
      min-width: 260px;
      box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
    }
    .table-dialog-title { margin: 0 0 1rem; font-size: 1.25rem; }
    .table-dialog-row { display: flex; gap: 1rem; margin-bottom: 1rem; align-items: center; }
    .table-dialog-row label { min-width: 5em; }
    .table-dialog-actions { display: flex; gap: 0.5rem; justify-content: flex-end; }
    .table-color-swatches { display: flex; flex-wrap: wrap; gap: 0.35rem; align-items: center; }
    .table-color-swatch {
      width: 1.5rem; height: 1.5rem; border-radius: 4px; border: 2px solid transparent;
      cursor: pointer; box-sizing: border-box;
    }
    .table-color-swatch:hover { border-color: var(--md-sys-color-outline, #79747e); }
    .table-color-swatch.selected { border-color: var(--md-sys-color-primary, #6750a4); }
    .table-color-swatch.transparent { background: repeating-conic-gradient(#ccc 0% 25%, #fff 0% 50%) 50% / 0.5rem 0.5rem; }
    .table-color-input-wrap { display: inline-flex; align-items: center; gap: 0.5rem; }
    .table-color-input-wrap input[type="color"] {
      width: 2rem; height: 1.75rem; padding: 0; border: 1px solid var(--md-sys-color-outline-variant, #cac4d0);
      border-radius: 4px; cursor: pointer; background: none;
    }
    .toolbar-color-swatches { display: inline-flex; gap: 2px; align-items: center; flex-wrap: wrap; }
    .toolbar-color-input { width: 1.5rem; height: 1.25rem; padding: 0; border: 1px solid rgba(0,0,0,0.2); border-radius: 3px; cursor: pointer; background: none; }
    .toolbar-color-btn { width: 1.5rem; height: 1.25rem; padding: 0; border: 2px solid rgba(0,0,0,0.2); border-radius: 3px; cursor: pointer; }
    .toolbar-color-btn:hover { border-color: var(--md-sys-color-primary, #6750a4); }
    .toolbar-color-swatch { width: 1.25rem; height: 1.25rem; border-radius: 3px; border: 2px solid rgba(0,0,0,0.2); cursor: pointer; }
    .toolbar-color-swatch:hover { transform: scale(1.1); }
    .toolbar-color-swatch.transparent { background: repeating-conic-gradient(#ccc 0% 25%, #fff 0% 50%) 50% / 4px 4px; }
    .toolbar-color-swatch.selected { border-color: var(--md-sys-color-primary, #6750a4); box-shadow: 0 0 0 1px var(--md-sys-color-primary, #6750a4); }
    .toolbar-table-margin { display: inline-flex; align-items: center; gap: 0.35rem; }
    .toolbar-margin-label { font-size: 0.75rem; color: var(--md-sys-color-on-surface-variant, #666); white-space: nowrap; }
    .toolbar-margin-select { font-size: 0.8rem; padding: 0.25em 0.5em; border-radius: 4px; border: 1px solid var(--md-sys-color-outline-variant, #cac4d0); background: var(--md-sys-color-surface, #fff); }
  `;

  static properties = {
    _editorView: { state: true },
    _manualContent: { state: true },
    _showXamixDialog: { state: true },
    _xamixInput: { state: true },
    _inlineXamix: { state: true },
    _xamixBlockLevel: { state: true },
    _xamixBold: { state: true },
    _xamixBrush: { state: true },
    _xamixColor: { state: true },
    _editingXamixPos: { state: true },
    _showTableDialog: { state: true },
    _tableRows: { state: true },
    _tableCols: { state: true },
    _tableMargin: { state: true },
    _tableBackgroundColor: { state: true },
    _tableBorderColor: { state: true },
    _fileHandle: { state: true },
    _currentPage: { state: true },
    _darkMode: { state: true },
    _showSvgDialog: { state: true },
    _svgDialogContent: { state: true },
  };

  declare _editorView: EditorView | null;
  declare _manualContent: ManualContent | null;
  declare _fileHandle: FileSystemFileHandle | null;
  declare _currentPage: number;
  private _persistDebounce: ReturnType<typeof setTimeout> | null = null;
  declare _showXamixDialog: boolean;
  declare _xamixInput: string;
  declare _inlineXamix: boolean;
  declare _xamixBlockLevel: 0 | 1 | 2 | 3;
  declare _xamixBold: boolean;
  declare _xamixBrush: boolean;
  declare _xamixColor: string;
  declare _editingXamixPos: number | null;
  declare _showTableDialog: boolean;
  declare _tableRows: number;
  declare _tableCols: number;
  declare _tableMargin: string;
  declare _tableBackgroundColor: string;
  declare _tableBorderColor: string;
  declare _darkMode: boolean;
  declare _showSvgDialog: boolean;
  declare _svgDialogContent: string;
  private _textColorPickr: Pickr | null = null;
  private _highlightColorPickr: Pickr | null = null;
  private _tableBgColorPickr: Pickr | null = null;
  private _tableBorderColorPickr: Pickr | null = null;
  private _cellBgColorPickr: Pickr | null = null;

  private _createColorPicker(
    btnSelector: string | HTMLElement,
    defaultColor: string,
    onChange: (color: string) => void
  ): Pickr {
    let btn: HTMLElement;
    if (typeof btnSelector === 'string') {
      // First try to find in main component's shadow DOM
      const root = this.renderRoot as ShadowRoot;
      btn = root.querySelector(btnSelector) as HTMLButtonElement;
      
      // If not found, try to find inside color-picker-button components' shadow DOMs
      if (!btn) {
        const colorPickerButtons = root.querySelectorAll('color-picker-button');
        for (const cpb of colorPickerButtons) {
          const shadowRoot = cpb.shadowRoot;
          if (shadowRoot) {
            btn = shadowRoot.querySelector(btnSelector) as HTMLButtonElement;
            if (btn) break;
          }
        }
      }
    } else {
      btn = btnSelector;
    }
    
    if (!btn) {
      throw new Error(`Button not found: ${typeof btnSelector === 'string' ? btnSelector : 'element'}`);
    }
    
    // Create a div next to the button for Pickr to use
    // This allows the original button to stay visible
    const pickrContainer = document.createElement('div');
    pickrContainer.className = 'pickr-container';
    btn.parentNode?.insertBefore(pickrContainer, btn.nextSibling);
    
    const pickr = Pickr.create({
      theme: 'monolith',
      default: defaultColor,
      el: pickrContainer,
      components: {
        preview: true,
        hue: true,
        interaction: {
          hex: true,
          rgba: false,
          input: true,
        },
      },
    });
    
    // Add eyedropper button after pickr is shown
    // Note: Uses native browser EyeDropper API which is only available in Chrome/Edge
    const addEyedropperButton = () => {
      // Check if button already exists in body
      const existingBtn = document.body.querySelector('.eyedropper-btn');
      if (existingBtn) {
        existingBtn.remove();
      }
      
      if ('EyeDropper' in window) {
        const eyedropperBtn = document.createElement('button');
        eyedropperBtn.className = 'eyedropper-btn';
        eyedropperBtn.type = 'button';
        eyedropperBtn.title = 'Seleccionar color de pantalla (EyeDropper)';
        eyedropperBtn.textContent = '🎯 Pick color from screen';
        
        // Find the pickr container position to position button nearby
        // Search in both document.body and within the component's shadow DOM
        let pickrEl = document.body.querySelector('.pickr-container');
        
        // If not found in body, try to find in the component's shadow DOM
        if (!pickrEl) {
          const root = (this.renderRoot as ShadowRoot);
          pickrEl = root.querySelector('.pickr-container');
        }
        
        // Default position: center of viewport
        let topPos = '100px';
        let leftPos = '50%';
        let transform = 'translateX(-50%)';
        
        if (pickrEl) {
          const rect = pickrEl.getBoundingClientRect();
          // Position below the pickr container
          topPos = `${rect.bottom + 10}px`;
          leftPos = `${rect.left}px`;
          transform = 'none';
        }
        
        // Style to appear as floating button below pickr
        eyedropperBtn.style.cssText = `
          position: fixed !important;
          top: ${topPos} !important;
          left: ${leftPos} !important;
          transform: ${transform} !important;
          padding: 8px 16px !important;
          cursor: pointer !important;
          border: 2px solid #666 !important;
          border-radius: 6px !important;
          background: #f5f5f5 !important;
          font-size: 14px !important;
          z-index: 999999 !important;
          color: #333 !important;
          font-weight: 500 !important;
          box-shadow: 0 2px 8px rgba(0,0,0,0.15) !important;
          font-family: system-ui, -apple-system, sans-serif !important;
        `;
        eyedropperBtn.addEventListener('click', async (e) => {
          e.stopPropagation();
          try {
            const eyeDropper = new (window as any).EyeDropper();
            const result = await eyeDropper.open();
            const color = result.sRGBHex;
            onChange(color);
            pickr.setColor(color);
            // Remove the floating button after use
            eyedropperBtn.remove();
          } catch (e) {
            // User cancelled or error
          }
        });
        document.body.appendChild(eyedropperBtn);
      } else {
        // Show message for browsers without EyeDropper support
        const noSupportMsg = document.createElement('div');
        noSupportMsg.className = 'eyedropper-msg';
        noSupportMsg.style.cssText = 'padding: 10px; font-size: 12px; color: #999; text-align: center; margin-top: 10px;';
        noSupportMsg.textContent = '🎯 EyeDropper not available in this browser';
        document.body.appendChild(noSupportMsg);
      }
    };
    
    // Add button when pickr is shown
    pickr.on('show', () => {
      addEyedropperButton();
    });
    
    // Also try with delay to ensure pickr DOM is ready
    setTimeout(addEyedropperButton, 500);
    
    pickr.on('change', (color: any) => {
      if (color && typeof color.toHEXA === 'function') {
        onChange(color.toHEXA().toString());
      }
    });
    pickr.on('save', (color: any) => {
      if (color && typeof color.toHEXA === 'function') {
        onChange(color.toHEXA().toString());
      }
      pickr.hide();
    });
    return pickr;
  }

  private _handleColorPickerClick(e: CustomEvent<{ colorType: string }>) {
    const { colorType } = e.detail;
    if (colorType === 'text') {
      this._showTextColorPicker();
    } else if (colorType === 'highlight') {
      this._showHighlightColorPicker();
    } else if (colorType === 'table-bg') {
      this._showTableBgColorPicker();
    } else if (colorType === 'table-border') {
      this._showTableBorderColorPicker();
    } else if (colorType === 'cell-bg') {
      this._showCellBgColorPicker();
    }
  }

  private _showTextColorPicker() {
    const ts = this._getToolbarState();
    if (this._textColorPickr) {
      this._textColorPickr.setColor(ts.textColor || '#000000');
      this._textColorPickr.show();
      return;
    }
    this._textColorPickr = this._createColorPicker(
      '.color-swatch[data-color-type="text"]',
      ts.textColor || '#000000',
      (color: string) => {
        if (this._editorView) {
          setTextColor(color)(this._editorView.state, this._editorView.dispatch);
          this.requestUpdate();
        }
      }
    );
    this._textColorPickr.show();
  }

  private _showHighlightColorPicker() {
    const ts = this._getToolbarState();
    if (this._highlightColorPickr) {
      this._highlightColorPickr.setColor(ts.highlightColor || '#fef08a');
      this._highlightColorPickr.show();
      return;
    }
    this._highlightColorPickr = this._createColorPicker(
      '.color-swatch[data-color-type="highlight"]',
      ts.highlightColor || '#fef08a',
      (color: string) => {
        if (this._editorView) {
          setHighlightColor(color)(this._editorView.state, this._editorView.dispatch);
          this.requestUpdate();
        }
      }
    );
    this._highlightColorPickr.show();
  }

  private _showTableBgColorPicker() {
    const ts = this._getToolbarState();
    if (this._tableBgColorPickr) {
      this._tableBgColorPickr.setColor(ts.tableBackgroundColor || '#ffffff');
      this._tableBgColorPickr.show();
      return;
    }
    this._tableBgColorPickr = this._createColorPicker(
      '.color-swatch[data-color-type="table-bg"]',
      ts.tableBackgroundColor || '#ffffff',
      (color: string) => {
        if (this._editorView) {
          setTableBackgroundColor(color)(this._editorView.state, this._editorView.dispatch);
          this.requestUpdate();
        }
      }
    );
    this._tableBgColorPickr.show();
  }

  private _showTableBorderColorPicker() {
    const ts = this._getToolbarState();
    if (this._tableBorderColorPickr) {
      this._tableBorderColorPickr.setColor(ts.tableBorderColor || '#cac4d0');
      this._tableBorderColorPickr.show();
      return;
    }
    this._tableBorderColorPickr = this._createColorPicker(
      '.color-swatch[data-color-type="table-border"]',
      ts.tableBorderColor || '#cac4d0',
      (color: string) => {
        if (this._editorView) {
          setTableBorderColor(color)(this._editorView.state, this._editorView.dispatch);
          this.requestUpdate();
        }
      }
    );
    this._tableBorderColorPickr.show();
  }

  private _showCellBgColorPicker() {
    const ts = this._getToolbarState();
    if (this._cellBgColorPickr) {
      this._cellBgColorPickr.setColor(ts.tableCellBackgroundColor || '#ffffff');
      this._cellBgColorPickr.show();
      return;
    }
    this._cellBgColorPickr = this._createColorPicker(
      '.color-swatch[data-color-type="cell-bg"]',
      ts.tableCellBackgroundColor || '#ffffff',
      (color: string) => {
        if (this._editorView) {
          setCellBackgroundColor(color)(this._editorView.state, this._editorView.dispatch);
          this.requestUpdate();
        }
      }
    );
    this._cellBgColorPickr.show();
  }

  constructor() {
    super();
    this._editorView = null;
    this._manualContent = null;
    this._showXamixDialog = false;
    this._xamixInput = '';
    this._inlineXamix = true;
    this._xamixBlockLevel = 0;
    this._xamixBold = false;
    this._xamixBrush = false;
    this._xamixColor = '';
    this._editingXamixPos = null;
    this._showTableDialog = false;
    this._tableRows = 3;
    this._tableCols = 3;
    this._tableMargin = '';
    this._tableBackgroundColor = '';
    this._tableBorderColor = '';
    this._fileHandle = null;
    this._currentPage = 0;
    this._darkMode = !!localStorage.getItem('nawatl-dark-mode');
    this._showSvgDialog = false;
    this._svgDialogContent = '';
  }

  private _toggleDarkMode = () => {
    this._darkMode = !this._darkMode;
    localStorage.setItem('nawatl-dark-mode', this._darkMode ? '1' : '');
  };

  private _setPageBackgroundColor(pageIndex: number, color: string) {
    if (!this._manualContent) return;
    const sec = this._manualContent.sections[pageIndex];
    if (!sec) return;
    this._manualContent.sections[pageIndex] = { ...sec, backgroundColor: color };
    this._persistToSession();
    this.requestUpdate();
  }

  private _setAllPagesBackgroundColor(color: string) {
    if (!this._manualContent) return;
    for (let i = 0; i < this._manualContent.sections.length; i++) {
      this._manualContent.sections[i] = { ...this._manualContent.sections[i], backgroundColor: color };
    }
    this._persistToSession();
    this.requestUpdate();
  }

  firstUpdated() {
    this.toggleAttribute('data-theme', this._darkMode);
    if (this._darkMode) {
      this.setAttribute('data-theme', 'dark');
      document.documentElement.setAttribute('data-theme', 'dark');
    } else {
      document.documentElement.removeAttribute('data-theme');
    }
    const restored = this._loadFromSession();
    if (restored) {
      this._manualContent = restored.content;
      this._currentPage = Math.min(restored.currentPage, restored.content.sections.length - 1);
    } else {
      this._manualContent = JSON.parse(JSON.stringify(SAMPLE_CONTENT));
    }

    this._createColorPicker
    this._initEditor(this._currentPage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this._persistDebounce) clearTimeout(this._persistDebounce);
    this._editorView?.destroy();
    // Clean up Pickr instances
    this._textColorPickr?.destroyAndRemove();
    this._highlightColorPickr?.destroyAndRemove();
    this._tableBgColorPickr?.destroyAndRemove();
    this._tableBorderColorPickr?.destroyAndRemove();
    this._cellBgColorPickr?.destroyAndRemove();
  }

  private _loadFromSession(): { content: ManualContent; currentPage: number } | null {
    try {
      const raw = sessionStorage.getItem(SESSION_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw) as { content: ManualContent; currentPage: number };
      if (!parsed?.content?.sections?.length) return null;
      parsed.content = normalizeContentAsPages(parsed.content);
      return parsed;
    } catch {
      return null;
    }
  }

  private _persistToSession() {
    if (!this._manualContent) return;
    this._flushCurrentPage();
    try {
      sessionStorage.setItem(
        SESSION_KEY,
        JSON.stringify({ content: this._manualContent, currentPage: this._currentPage })
      );
    } catch {}
  }

  private _schedulePersist() {
    if (this._persistDebounce) clearTimeout(this._persistDebounce);
    this._persistDebounce = setTimeout(() => {
      this._persistDebounce = null;
      this._persistToSession();
    }, 400);
  }

  private _initEditor(pageIndex: number) {
    const container = this.shadowRoot?.querySelector('.editor-container');
    if (!container || !this._manualContent) return;

    if (this._editorView) {
      this._editorView.destroy();
      this._editorView = null;
    }

    const section = this._manualContent.sections[pageIndex];
    const blocks = section?.blocks ?? [];
    const doc = manualToDocFromBlocks(blocks);

    this._editorView = createEditor({
      element: container as HTMLElement,
      initialDoc: doc,
      onUpdate: () => {
        this.requestUpdate();
        this._schedulePersist();
      },
    });
    this._editorView.dom.classList.add('editor-content');
    this._currentPage = pageIndex;
    this.requestUpdate(); // Refresh toolbar state for new page
  }

  private _getToolbarState(): {
    blockType: 'paragraph' | 'heading' | 'centered';
    headingLevel: 1 | 2 | 3 | null;
    hasBold: boolean;
    hasItalic: boolean;
    textColor: string | null;
    highlightColor: string | null;
    listType: 'bullet' | 'ordered' | null;
    inTable: boolean;
    tableMargin: string | null;
    tableBackgroundColor: string | null;
    tableBorderColor: string | null;
    tableCellBackgroundColor: string | null;
  } {
    const view = this._editorView;
    if (!view?.state) {
      return { blockType: 'paragraph', headingLevel: null, hasBold: false, hasItalic: false, textColor: null, highlightColor: null, listType: null, inTable: false, tableMargin: null, tableBackgroundColor: null, tableBorderColor: null, tableCellBackgroundColor: null };
    }
    const { state } = view;
    const { $from } = state.selection;
    const parent = $from.parent;
    const marks = state.storedMarks ?? $from.marks();

    let blockType: 'paragraph' | 'heading' | 'centered' = 'paragraph';
    let headingLevel: 1 | 2 | 3 | null = null;
    if (parent.type.name === 'heading') {
      blockType = 'heading';
      headingLevel = (parent.attrs.level as number) as 1 | 2 | 3;
    } else if (parent.type.name === 'centered') {
      blockType = 'centered';
    }

    let listType: 'bullet' | 'ordered' | null = null;
    for (let d = $from.depth; d >= 0; d--) {
      const node = $from.node(d);
      if (node.type.name === 'bullet_list') {
        listType = 'bullet';
        break;
      }
      if (node.type.name === 'ordered_list') {
        listType = 'ordered';
        break;
      }
    }

    const inTable = isInTable(state);
    const tableMargin = inTable ? getTableMargin(state) : null;
    const tableBackgroundColor = inTable ? getTableBackgroundColor(state) : null;
    const tableBorderColor = inTable ? getTableBorderColor(state) : null;
    const tableCellBackgroundColor = inTable ? getCellBackgroundColor(state) : null;
    const textColor = getTextColor(state);
    const highlightColor = getHighlightColor(state);

    return {
      blockType,
      headingLevel,
      hasBold: state.schema.marks.bold.isInSet(marks) != null,
      hasItalic: state.schema.marks.italic.isInSet(marks) != null,
      textColor,
      highlightColor,
      listType,
      inTable,
      tableMargin,
      tableBackgroundColor,
      tableBorderColor,
      tableCellBackgroundColor,
    };
  }

  private _handleParagraph = () => {
    // ProseMirror default is paragraph; we could toggle block type
    this._editorView?.focus();
  };

  private _handleHeading = (level: 1 | 2 | 3) => {
    const { state, dispatch } = this._editorView!;
    const { $from } = state.selection;
    const node = $from.parent;
    if (node.type.name === 'heading' && node.attrs.level === level) return;
    const tr = state.tr.setBlockType($from.before(), $from.after(), state.schema.nodes.heading, { level });
    dispatch?.(tr);
  };

  private _handleBold = () => {
    const { state, dispatch } = this._editorView!;
    toggleMark(state.schema.marks.bold)(state, dispatch);
  };

  private _handleItalic = () => {
    const { state, dispatch } = this._editorView!;
    toggleMark(state.schema.marks.italic)(state, dispatch);
  };

  private _handleInsertEmsp = () => {
    const { state, dispatch } = this._editorView!;
    const { from, to } = state.selection;
    const tr = state.tr.insertText('\u2003', from, to);
    dispatch?.(tr);
  };

  private _showImageDialog = false;
  private _imageDialogSrc = '';
  private _imageDialogAlt = '';
  private _imageDialogWidth = '';
  private _imageDialogHeight = '';

  private _handleInsertImage = () => {
    this._showImageDialog = true;
    this._imageDialogSrc = '';
    this._imageDialogAlt = '';
    this._imageDialogWidth = '';
    this._imageDialogHeight = '';
    this.requestUpdate();
  };

  private _handleInsertImageFromFile = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.addEventListener('change', () => {
      const file = input.files?.[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = () => {
        const dataUrl = reader.result as string;
        this._showImageDialog = true;
        this._imageDialogSrc = dataUrl;
        this._imageDialogAlt = file.name.replace(/\.[^.]+$/, '');
        this._imageDialogWidth = '';
        this._imageDialogHeight = '';
        this.requestUpdate();
      };
      reader.readAsDataURL(file);
    });
    input.click();
  };

  private _confirmImageDialog = () => {
    if (!this._imageDialogSrc || !this._editorView) return;
    const w = this._imageDialogWidth ? parseInt(this._imageDialogWidth, 10) : null;
    const h = this._imageDialogHeight ? parseInt(this._imageDialogHeight, 10) : null;
    insertInlineImage(this._imageDialogSrc, this._imageDialogAlt, '', w, h)(this._editorView.state, this._editorView.dispatch);
    this._showImageDialog = false;
    this.requestUpdate();
    this._editorView.focus();
  };

  private _cancelImageDialog = () => {
    this._showImageDialog = false;
    this.requestUpdate();
  };

  private _handleInsertSvg = () => {
    this._showSvgDialog = true;
    this._svgDialogContent = '';
    this.requestUpdate();
  };

  private _handleSvgFileSelect = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.svg,image/svg+xml';
    input.addEventListener('change', () => {
      const file = input.files?.[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = () => {
        this._svgDialogContent = reader.result as string;
        this.requestUpdate();
      };
      reader.readAsText(file);
    });
    input.click();
  };

  private _confirmSvgDialog = () => {
    if (!this._svgDialogContent.trim() || !this._editorView) return;
    insertInlineSvg(this._svgDialogContent)(this._editorView.state, this._editorView.dispatch);
    this._showSvgDialog = false;
    this._svgDialogContent = '';
    this.requestUpdate();
    this._editorView.focus();
  };

  private _cancelSvgDialog = () => {
    this._showSvgDialog = false;
    this._svgDialogContent = '';
    this.requestUpdate();
    this._editorView?.focus();
  };

  private _handleBulletList = () => {
    wrapBulletList(this._editorView!.state, this._editorView!.dispatch);
  };

  private _handleOrderedList = () => {
    wrapOrderedList(this._editorView!.state, this._editorView!.dispatch);
  };

  private _handleInsertTable = () => {
    this._tableRows = 3;
    this._tableCols = 3;
    this._tableMargin = '';
    this._tableBackgroundColor = '';
    this._tableBorderColor = '';
    this._showTableDialog = true;
  };

  private _confirmTable = () => {
    const rows = Math.max(1, Math.min(20, this._tableRows));
    const cols = Math.max(1, Math.min(10, this._tableCols));
    insertTable(rows, cols, this._tableMargin, this._tableBackgroundColor, this._tableBorderColor)(this._editorView!.state, this._editorView!.dispatch);
    this._showTableDialog = false;
    this._editorView?.focus();
  };

  private _cancelTable = () => {
    this._showTableDialog = false;
    this._editorView?.focus();
  };

  private _handleDeleteTable = () => {
    if (!this._editorView) return;
    deleteTable(this._editorView.state, this._editorView.dispatch);
  };

  private _handlePageBreak = () => {
    if (!this._editorView || !this._manualContent) return;
    const { state } = this._editorView;
    const pos = state.selection.$from.pos;
    const blocksBefore = editorStateToBlocks(
      state.apply(state.tr.delete(pos, state.doc.content.size)),
      this._editorView
    );
    const slice = state.doc.slice(pos);
    const blocksAfter = blocksFromSlice(slice, this._editorView);
    const prev = this._manualContent.sections[this._currentPage];
    this._manualContent.sections[this._currentPage] = { ...prev, blocks: blocksBefore };
    this._manualContent.sections.splice(this._currentPage + 1, 0, {
      blocks: blocksAfter.length > 0 ? blocksAfter : [],
    });
    this._initEditor(this._currentPage + 1);
    this._persistToSession();
  };

  private _handleGoToPage = (pageIndex: number) => {
    if (!this._editorView || !this._manualContent || pageIndex === this._currentPage) return;
    const blocks = editorStateToBlocks(this._editorView.state, this._editorView);
    const prev = this._manualContent.sections[this._currentPage];
    this._manualContent.sections[this._currentPage] = { ...prev, blocks };
    this._currentPage = pageIndex;
    this._initEditor(pageIndex);
    this._persistToSession();
  };

  private _handleDeletePage = async (pageIndex: number) => {
    if (!this._manualContent || this._manualContent.sections.length <= 1) return;
    const confirmed = window.confirm(
      `¿Eliminar página ${pageIndex + 1}? Esta acción no se puede deshacer.`
    );
    if (!confirmed) return;
    this._flushCurrentPage();
    this._manualContent.sections.splice(pageIndex, 1);
    let newPage: number;
    if (pageIndex < this._currentPage) {
      newPage = this._currentPage - 1;
    } else if (pageIndex === this._currentPage) {
      newPage = pageIndex > 0 ? pageIndex - 1 : 0;
    } else {
      newPage = this._currentPage;
    }
    this._initEditor(newPage);
    this._persistToSession();
  };

  private _handleEditorClick = (e: MouseEvent) => {
    const target = e.target as Node;
    if (!(target instanceof Element) || !this._editorView) return;
    const blockWrapper = target.closest?.('.block-xamix');
    const xamixEl = target.closest?.('xamix-element');
    const nodeDom = blockWrapper ?? xamixEl;
    if (!nodeDom) return;
    const pos = this._editorView.posAtDOM(nodeDom as Node, 0);
    const node = this._editorView.state.doc.nodeAt(pos);
    if (!node || (node.type.name !== 'xamix' && node.type.name !== 'block_xamix')) return;
    e.preventDefault();
    e.stopPropagation();
    this._editingXamixPos = pos;
    this._xamixInput = (node.attrs.textInput as string) ?? '';
    this._inlineXamix = node.type.name === 'xamix';
    this._xamixBlockLevel = ((node.attrs.level as number) ?? 0) as 0 | 1 | 2 | 3;
    const hasBrush = !!(node.attrs.brush as boolean);
    const hasBold = !!(node.attrs.bold as boolean);
    this._xamixBrush = hasBrush;
    this._xamixBold = hasBold && !hasBrush;
    this._xamixColor = (node.attrs as { color?: string }).color || '';
    this._showXamixDialog = true;
  };

  private _handleInsertXamix = () => {
    this._editingXamixPos = null;
    this._xamixInput = '';
    this._showXamixDialog = true;
    this._inlineXamix = true;
  };

  private _handleInsertBlockXamix = () => {
    this._editingXamixPos = null;
    this._xamixInput = '';
    this._showXamixDialog = true;
    this._inlineXamix = false;
    this._xamixBlockLevel = 0;
  };

  private _handleInsertXamixHeading = (level: 1 | 2 | 3) => {
    this._editingXamixPos = null;
    this._xamixInput = '';
    this._showXamixDialog = true;
    this._inlineXamix = false;
    this._xamixBlockLevel = level;
  };

  private _confirmXamix = () => {
    const text = this._xamixInput.trim();
    if (!text || !this._editorView) return;
    if (this._editingXamixPos != null) {
      const { state, dispatch } = this._editorView;
      const node = state.doc.nodeAt(this._editingXamixPos);
      if (!node) return;
      const attrs = { ...node.attrs, textInput: text, bold: this._xamixBold, brush: this._xamixBrush, color: this._xamixColor || null };
      if (node.type.name === 'block_xamix') {
        (attrs as { level?: number }).level = this._xamixBlockLevel;
      }
      const tr = state.tr.setNodeMarkup(this._editingXamixPos, null, attrs);
      dispatch?.(tr);
      this._editingXamixPos = null;
    } else {
      const cmd = this._inlineXamix ? insertInlineXamix(text, this._xamixBold, this._xamixBrush, this._xamixColor || null) : insertBlockXamix(text, this._xamixBlockLevel, this._xamixBold, this._xamixBrush, this._xamixColor || null);
      cmd(this._editorView.state, this._editorView.dispatch);
    }
    this._editorView.focus();
    this._showXamixDialog = false;
    this._xamixInput = '';
  };

  private _cancelXamix = () => {
    this._showXamixDialog = false;
    this._xamixInput = '';
    this._editingXamixPos = null;
    this._editorView?.focus();
  };

  private async _handleOpenFile() {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json';
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;
      try {
        const text = await file.text();
        const parsed = JSON.parse(text) as ManualContent;
        this._manualContent = normalizeContentAsPages(parsed);
        this._currentPage = 0;
        this._initEditor(0);
        this._persistToSession();
        if ('showOpenFilePicker' in window) {
          this._fileHandle = null;
        }
      } catch (e) {
        console.error('Failed to load JSON:', e);
        alert('No se pudo cargar el archivo.');
      }
    };
    input.click();
  }

  private _flushCurrentPage() {
    if (this._editorView && this._manualContent) {
      const blocks = editorStateToBlocks(this._editorView.state, this._editorView);
      const prev = this._manualContent.sections[this._currentPage];
      this._manualContent.sections[this._currentPage] = { ...prev, blocks };
    }
  }

  private async _handleSaveFile() {
    if (!this._manualContent) return;
    this._flushCurrentPage();
    const blob = new Blob([JSON.stringify(this._manualContent, null, 2)], { type: 'application/json' });
    if ('showSaveFilePicker' in window) {
      try {
        const handle = await (window as unknown as { showSaveFilePicker: (opts: { suggestedName?: string; types?: any }) => Promise<FileSystemFileHandle> }).showSaveFilePicker({
          suggestedName: 'manual.json',
          types: [{ description: 'JSON', accept: { 'application/json': ['.json'] } }],
        });
        const writable = await handle.createWritable();
        await writable.write(blob);
        await writable.close();
        this._fileHandle = handle;
      } catch (e) {
        if ((e as Error).name !== 'AbortError') {
          alert('No se pudo guardar.');
        }
      }
    } else {
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = 'manual.json';
      a.click();
      URL.revokeObjectURL(a.href);
    }
  }

  private async _handleSaveAsFile() {
    this._fileHandle = null;
    await this._handleSaveFile();
  }

  private async _handleSave() {
    if (this._fileHandle) {
      if (!this._manualContent) return;
      this._flushCurrentPage();
      const blob = new Blob([JSON.stringify(this._manualContent, null, 2)], { type: 'application/json' });
      try {
        const writable = await this._fileHandle.createWritable();
        await writable.write(blob);
        await writable.close();
      } catch (e) {
        alert('No se pudo guardar. Intenta Guardar como.');
      }
    } else {
      await this._handleSaveFile();
    }
  }

  private async _handleExportPdf() {
    if (!this._manualContent) return;
    this._flushCurrentPage();
    const printDoc = createPrintDocument(this._manualContent);
    const overlay = document.createElement('div');
    overlay.id = 'pdf-print-overlay';
    overlay.style.cssText = 'position:fixed;inset:0;background:#fff;z-index:99999;overflow:auto;padding:2rem;';
    overlay.appendChild(printDoc);
    document.body.appendChild(overlay);
    const cleanup = () => {
      overlay.remove();
      window.removeEventListener('afterprint', cleanup);
    };
    window.addEventListener('afterprint', cleanup);
    await this._waitForXamixRender(overlay);
    window.print();
  }

  private _waitForXamixRender(overlay: HTMLElement, timeout = 4000): Promise<void> {
    return new Promise((resolve) => {
      const xamixEls = overlay.querySelectorAll('xamix-element');
      const check = (): boolean => {
        if (xamixEls.length === 0) return true;
        for (const el of xamixEls) {
          const container = (el as Element & { shadowRoot?: ShadowRoot }).shadowRoot?.getElementById('text-output-container');
          if (!container || container.children.length === 0) return false;
        }
        return true;
      };
      const start = Date.now();
      const tick = () => {
        if (check() || Date.now() - start > timeout) {
          resolve();
          return;
        }
        setTimeout(tick, 80);
      };
      requestAnimationFrame(() => setTimeout(tick, 150));
    });
  }

  private _handleNew = () => {
    sessionStorage.removeItem(SESSION_KEY);
    this._manualContent = JSON.parse(JSON.stringify(SAMPLE_CONTENT));
    this._currentPage = 0;
    this._initEditor(0);
  };

  updated(changed: Map<string, unknown>) {
    super.updated(changed);
    if (changed.has('_darkMode')) {
      if (this._darkMode) {
        this.setAttribute('data-theme', 'dark');
        document.documentElement.setAttribute('data-theme', 'dark');
      } else {
        this.removeAttribute('data-theme');
        document.documentElement.removeAttribute('data-theme');
      }
    }
  }

  render() {
    const pageCount = this._manualContent?.sections.length ?? 0;
    const showPagePanel = pageCount > 1;

    return html`
      <div class="layout">
        ${showPagePanel
          ? html`
              <aside class="page-panel">
                <h4>Paginas</h4>
                <p class="page-panel-hint" style="font-size:0.75rem;color:#999;margin:0 0 0.5rem">Color de página:</p>
                <ul class="page-list">
                  ${this._manualContent!.sections.map(
                    (sec, i) => html`
                      <li>
                        <input type="color" class="page-color-btn" title="Color de fondo página ${i + 1}"
                          style=${sec.backgroundColor ? `background-color:${sec.backgroundColor}` : ''}
                          .value=${sec.backgroundColor && /^#[0-9A-Fa-f]{6}$/.test(sec.backgroundColor) ? sec.backgroundColor : '#ffffff'}
                          @input=${(e: Event) => this._setPageBackgroundColor(i, (e.target as HTMLInputElement).value)}
                        >
                        ${this._currentPage === i
                          ? html`<md-filled-tonal-button type="button" @click=${() => this._handleGoToPage(i)}>Pagina ${i + 1}</md-filled-tonal-button>`
                          : html`<md-outlined-button type="button" @click=${() => this._handleGoToPage(i)}>Pagina ${i + 1}</md-outlined-button>`
                        }
                        <md-icon-button
                          type="button"
                          aria-label="Eliminar página"
                          ?disabled=${this._manualContent!.sections.length <= 1}
                          @click=${(e: Event) => {
                            e.stopPropagation();
                            this._handleDeletePage(i);
                          }}
                        >
                          <md-icon>close</md-icon>
                        </md-icon-button>
                      </li>
                    `
                  )}
                </ul>
                <div style="margin-top:0.5rem;display:flex;align-items:center;gap:0.5rem">
                  <span style="font-size:0.75rem;color:#999">Todas:</span>
                  <input type="color" style="width:1.5rem;height:1.25rem;padding:0;border:1px solid #ccc;border-radius:4px;cursor:pointer" .value=${(this._manualContent!.sections[0]?.backgroundColor && /^#[0-9A-Fa-f]{6}$/.test(this._manualContent!.sections[0].backgroundColor)) ? this._manualContent!.sections[0].backgroundColor : '#ffffff'} @input=${(e: Event) => this._setAllPagesBackgroundColor((e.target as HTMLInputElement).value)}>
                </div>
              </aside>
            `
          : ''}
        <div class="app" style=${((): string => { const bg = this._manualContent?.sections[this._currentPage]?.backgroundColor; if (!bg || bg === '') return ''; const norm = bg.toLowerCase().replace(/\s/g, ''); if (norm === '#ffffff' || norm === '#fff') return ''; return `background:${bg}`; })()}>
        <div class="toolbar">
          ${((): ReturnType<typeof html> => {
            const ts = this._getToolbarState();
            return html`
              <md-outlined-icon-button type="button" toggle ?selected=${ts.blockType === 'paragraph' && !ts.listType} @click=${this._handleParagraph} aria-label="Párrafo"><md-icon>format_paragraph</md-icon></md-outlined-icon-button>
              <md-outlined-icon-button type="button" toggle ?selected=${ts.headingLevel === 1} @click=${() => this._handleHeading(1)} aria-label="Título 1"><md-icon>format_size</md-icon></md-outlined-icon-button>
              <md-outlined-icon-button type="button" toggle ?selected=${ts.headingLevel === 2} @click=${() => this._handleHeading(2)} aria-label="Título 2"><md-icon>looks_two</md-icon></md-outlined-icon-button>
              <md-outlined-icon-button type="button" toggle ?selected=${ts.headingLevel === 3} @click=${() => this._handleHeading(3)} aria-label="Título 3"><md-icon>looks_3</md-icon></md-outlined-icon-button>
              <span class="toolbar-sep"></span>
              <md-outlined-icon-button type="button" toggle ?selected=${ts.hasBold} @click=${this._handleBold} aria-label="Negrita"><md-icon>format_bold</md-icon></md-outlined-icon-button>
              <md-outlined-icon-button type="button" toggle ?selected=${ts.hasItalic} @click=${this._handleItalic} aria-label="Cursiva"><md-icon>format_italic</md-icon></md-outlined-icon-button>
              <color-picker-button
                colorType="text"
                label="Texto"
                backgroundColor=${ts.textColor || '#000000'}
                @color-picker-click=${this._handleColorPickerClick}
              ></color-picker-button>
              <color-picker-button
                colorType="highlight"
                label="Resaltar"
                backgroundColor=${ts.highlightColor || '#fef08a'}
                @color-picker-click=${this._handleColorPickerClick}
              ></color-picker-button>
              <md-outlined-icon-button type="button" @click=${this._handleInsertEmsp} aria-label="Em space (&emsp;)"><md-icon>space_bar</md-icon></md-outlined-icon-button>
              <md-outlined-icon-button type="button" @click=${() => insertHardBreak()(this._editorView!.state, this._editorView!.dispatch)} aria-label="Salto de línea (Shift+Enter)"><md-icon>keyboard_return</md-icon></md-outlined-icon-button>
              <md-outlined-icon-button type="button" @click=${this._handleInsertImageFromFile} aria-label="Insertar imagen"><md-icon>image</md-icon></md-outlined-icon-button>
              <md-outlined-icon-button type="button" @click=${this._handleInsertImage} aria-label="Insertar imagen por URL"><md-icon>add_photo_alternate</md-icon></md-outlined-icon-button>
              <md-outlined-icon-button type="button" @click=${this._handleInsertSvg} aria-label="Insertar SVG"><md-icon>draw</md-icon></md-outlined-icon-button>
              <span class="toolbar-sep"></span>
              <md-outlined-icon-button type="button" toggle ?selected=${ts.listType === 'bullet'} @click=${this._handleBulletList} aria-label="Lista con viñetas"><md-icon>format_list_bulleted</md-icon></md-outlined-icon-button>
              <md-outlined-icon-button type="button" toggle ?selected=${ts.listType === 'ordered'} @click=${this._handleOrderedList} aria-label="Lista numerada"><md-icon>format_list_numbered</md-icon></md-outlined-icon-button>
              <span class="toolbar-sep"></span>
              <md-outlined-icon-button type="button" @click=${this._handleInsertTable} aria-label="Insertar tabla"><md-icon>table_chart</md-icon></md-outlined-icon-button>
              ${ts.inTable
                ? html`
                    <span class="toolbar-table-margin">
                      <label for="table-margin-toolbar" class="toolbar-margin-label">Margen</label>
                      <select
                        id="table-margin-toolbar"
                        class="toolbar-margin-select"
                        .value=${ts.tableMargin ?? ''}
                        @change=${(e: Event) => {
                          const v = (e.target as HTMLSelectElement).value;
                          setTableMargin(v)(this._editorView!.state, this._editorView!.dispatch);
                        }}
                      >
                        <option value="">Ninguno</option>
                        <option value="0.5em">0.5em</option>
                        <option value="1em">1em</option>
                        <option value="1.5em">1.5em</option>
                        <option value="2em">2em</option>
                        ${ts.tableMargin && !['', '0.5em', '1em', '1.5em', '2em'].includes(ts.tableMargin)
                          ? html`<option value=${ts.tableMargin} selected>${ts.tableMargin}</option>`
                          : ''}
                      </select>
                    </span>
                    <span class="toolbar-margin-label">Tabla</span>
                    <color-picker-button
                      colorType="table-bg"
                      label="Tabla"
                      backgroundColor=${ts.tableBackgroundColor || '#ffffff'}
                      @color-picker-click=${this._handleColorPickerClick}
                    ></color-picker-button>
                    <color-picker-button
                      colorType="table-border"
                      label="Borde"
                      backgroundColor=${ts.tableBorderColor || '#cac4d0'}
                      @color-picker-click=${this._handleColorPickerClick}
                    ></color-picker-button>
                    <color-picker-button
                      colorType="cell-bg"
                      label="Celda"
                      backgroundColor=${ts.tableCellBackgroundColor || '#ffffff'}
                      @color-picker-click=${this._handleColorPickerClick}
                    ></color-picker-button>
                    <span class="toolbar-sep"></span>
                    <md-outlined-icon-button type="button" @click=${() => addColumnBeforeCmd(this._editorView!.state, this._editorView!.dispatch)} aria-label="Agregar columna a la izquierda"><md-icon>${svg`<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="m 21,3 h -3 m 3,18 h -3 m 0,0 H 14 A 2,2 0 0 1 12,19 V 9 m 6,12 V 9 m 0,-6 h -4 a 2,2 0 0 0 -2,2 v 4 m 6,-6 v 6 m 3,0 h -3 m 0,0 h -6 m 9,6 H 12 M 9,12 H 6 m 0,0 H 3 m 3,0 v 3 M 6,12 V 9"/></svg>`}</md-icon></md-outlined-icon-button>
                    <md-outlined-icon-button type="button" @click=${() => addColumnAfterCmd(this._editorView!.state, this._editorView!.dispatch)} aria-label="Agregar columna a la derecha"><md-icon>${svg`<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M3 3h3M3 21h3m0 0h4a2 2 0 0 0 2-2V9M6 21V9m0-6h4a2 2 0 0 1 2 2v4M6 3v6M3 9h3m0 0h6m-9 6h9m3-3h3m0 0h3m-3 0v3m0-3V9"/></svg>`}</md-icon></md-outlined-icon-button>
                    <md-outlined-icon-button type="button" @click=${() => addRowBeforeCmd(this._editorView!.state, this._editorView!.dispatch)} aria-label="Agregar fila arriba"><md-icon>${svg`<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="m 3,21 v -3 m 18,3 v -3 m 0,0 V 14 A 2,2 0 0 0 19,12 H 9 m 12,6 H 9 m -6,0 v -4 a 2,2 0 0 1 2,-2 h 4 m -6,6 h 6 m 0,3 v -3 m 0,0 v -6 m 6,9 V 12 M 12,9 V 6 m 0,0 V 3 m 0,3 h 3 M 12,6 H 9"/></svg>`}</md-icon></md-outlined-icon-button>
                    <md-outlined-icon-button type="button" @click=${() => addRowAfterCmd(this._editorView!.state, this._editorView!.dispatch)} aria-label="Agregar fila abajo"><md-icon>${svg`<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M3 3v3m18-3v3m0 0v4a2 2 0 0 1-2 2H9m12-6H9M3 6v4a2 2 0 0 0 2 2h4M3 6h6m0-3v3m0 0v6m6-9v9m-3 3v3m0 0v3m0-3h3m-3 0H9"/></svg>`}</md-icon></md-outlined-icon-button>
                    <md-outlined-icon-button type="button" @click=${() => removeColumnCmd(this._editorView!.state, this._editorView!.dispatch)} aria-label="Eliminar columna"><md-icon>${svg`<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M 3,3 H 6 M 3,21 h 3 m 0,0 h 4 c 1.104569,0 2,-0.895431 2,-2 V 9 M 6,21 V 9 M 6,3 h 4 c 1.104569,0 2,0.895431 2,2 V 9 M 6,3 V 9 M 3,9 h 3 m 0,0 h 6 m -9,6 h 9 m 3,-3 h 3 m 0,0 h 3"/></svg>`}</md-icon></md-outlined-icon-button>
                    <md-outlined-icon-button type="button" @click=${() => removeRowCmd(this._editorView!.state, this._editorView!.dispatch)} aria-label="Eliminar fila"><md-icon>${svg`<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M 3,3 V 6 M 21,3 v 3 m 0,0 v 4 c 0,1.104569 -0.895431,2 -2,2 H 9 M 21,6 H 9 M 3,6 v 4 c 0,1.104569 0.8954305,2 2,2 H 9 M 3,6 H 9 M 9,3 v 3 m 0,0 v 6 m 6,-9 v 9 m -3,6 h 3 m -3,0 H 9"/></svg>`}</md-icon></md-outlined-icon-button>
                    <span class="toolbar-sep"></span>
                    <md-outlined-icon-button type="button" @click=${() => mergeCellsCmd(this._editorView!.state, this._editorView!.dispatch)} aria-label="Combinar celdas"><md-icon>merge_type</md-icon></md-outlined-icon-button>
                    <md-outlined-icon-button type="button" @click=${() => splitCellCmd(this._editorView!.state, this._editorView!.dispatch)} aria-label="Dividir celda"><md-icon>split_cell</md-icon></md-outlined-icon-button>
                    <span class="toolbar-sep"></span>
                    <md-outlined-icon-button type="button" @click=${this._handleDeleteTable} aria-label="Eliminar tabla"><md-icon>delete</md-icon></md-outlined-icon-button>
                  `
                : ''}
            `;
          })()}
          <md-outlined-icon-button type="button" @click=${this._handlePageBreak} aria-label="Salto de página"><md-icon>vertical_split</md-icon></md-outlined-icon-button>
          <span class="toolbar-sep"></span>
          <md-outlined-icon-button type="button" @click=${this._handleInsertXamix} aria-label="Xamix inline"><md-icon>${svg`<svg viewBox="-2 -5 208 155" fill="currentColor"><path d="m 177.83269,12.654149 c 15.68503,-12.73354621 18.84196,-15.1710492 23.97241,23.714676 7.03065,53.288146 3.87682,62.2102 -1.31236,75.187275 -27.6725,69.20326 -151.046523,56.62212 -182.23997,8.47229 C 7.5023284,103.43414 0.58178581,90.179283 1.3791012,68.933914 2.1472679,48.46524 9.2429873,30.117281 67.803348,27.322716 c 29.131137,-1.390171 42.404522,9.382742 50.604032,18.177536 7.35559,7.889612 6.55598,37.797942 -16.19145,60.696568 C 91.704637,116.77799 56.141072,120.08011 56.141072,120.08011 129.0346,65.159384 65.689259,41.61328 53.633089,44.78372 41.287145,48.030363 41.340799,61.906597 42.067724,69.476011 46.285512,113.39549 81.201578,145.73292 155.3598,139.73633 174.68792,138.17341 170.88159,111.60596 165.93911,76.564701 156.38564,8.8323695 145.27953,39.081715 177.83269,12.654149 Z"/></svg>`}</md-icon></md-outlined-icon-button>
          <md-outlined-icon-button type="button" @click=${this._handleInsertBlockXamix} aria-label="Xamix bloque"><md-icon>${svg`<svg viewBox="-2 -12 210 330" fill="currentColor"><path d="m 177.83269,18.879966 c 15.68503,-22.2167799 18.84196,-26.4695987 23.97241,41.376038 7.03065,92.974176 3.87682,108.540876 -1.31236,131.182556 C 172.82024,312.18053 49.446217,290.22966 18.25277,206.22053 7.5023284,177.26781 0.58178581,154.14148 1.3791012,117.07375 2.1472679,81.361153 9.2429873,49.34866 67.803348,44.472859 96.934485,42.047368 110.20787,60.843344 118.40738,76.188006 125.76297,89.953362 124.96336,142.13574 102.21593,182.08799 91.704637,200.54941 56.141072,206.31077 56.141072,206.31077 129.0346,110.48816 65.689259,69.406236 53.633089,74.937842 41.287145,80.602403 41.340799,104.81288 42.067724,118.01957 c 4.217788,76.62825 39.133854,133.0488 113.292076,122.58627 19.32812,-2.72689 15.52179,-49.0803 10.57931,-110.21832 C 156.38564,12.211938 145.27953,64.989303 177.83269,18.879966 Z"/></svg>`}</md-icon></md-outlined-icon-button>
          <md-outlined-icon-button type="button" @click=${() => this._handleInsertXamixHeading(1)} aria-label="Xamix H1"><md-icon>looks_one</md-icon></md-outlined-icon-button>
          <md-outlined-icon-button type="button" @click=${() => this._handleInsertXamixHeading(2)} aria-label="Xamix H2"><md-icon>looks_two</md-icon></md-outlined-icon-button>
          <md-outlined-icon-button type="button" @click=${() => this._handleInsertXamixHeading(3)} aria-label="Xamix H3"><md-icon>looks_3</md-icon></md-outlined-icon-button>
          <span class="toolbar-sep"></span>
          <md-filled-icon-button type="button" @click=${this._handleOpenFile} aria-label="Abrir"><md-icon>folder_open</md-icon></md-filled-icon-button>
          <md-filled-icon-button type="button" @click=${this._handleSave} aria-label="Guardar"><md-icon>save</md-icon></md-filled-icon-button>
          <md-filled-icon-button type="button" @click=${this._handleSaveAsFile} aria-label="Guardar como"><md-icon>save_as</md-icon></md-filled-icon-button>
          <md-filled-icon-button type="button" @click=${this._handleExportPdf} aria-label="Exportar PDF"><md-icon>picture_as_pdf</md-icon></md-filled-icon-button>
          <md-outlined-icon-button type="button" @click=${this._handleNew} aria-label="Nuevo"><md-icon>note_add</md-icon></md-outlined-icon-button>
          <md-outlined-icon-button type="button" toggle ?selected=${this._darkMode} @click=${this._toggleDarkMode} aria-label="Modo oscuro"><md-icon>${this._darkMode ? 'light_mode' : 'dark_mode'}</md-icon></md-outlined-icon-button>
        </div>

        <div class="editor-container" tabindex="0" @click=${this._handleEditorClick}>
          <style>
            /* Table borders - .manual-table is set in schema; fallback for any table in editor */
            .editor-container table { border-collapse: collapse !important; }
            .editor-container table td,
            .editor-container table th {
              border: 1px solid #cac4d0 !important;
              box-sizing: border-box;
            }
            .editor-container table[data-table-border-color] td,
            .editor-container table[data-table-border-color] th {
              border-color: var(--table-border-color) !important;
            }
          </style>
        </div>

        <div class="actions"></div>

        ${this._showImageDialog
          ? html`
              <div class="xamix-dialog-backdrop" @click=${this._cancelImageDialog}>
                <div class="xamix-dialog" @click=${(e: Event) => e.stopPropagation()}>
                  <h3 class="xamix-dialog-title">Insertar imagen por URL</h3>
                  <md-outlined-text-field
                    label="URL de la imagen"
                    .value=${this._imageDialogSrc}
                    @input=${(e: Event) => { this._imageDialogSrc = (e.target as HTMLInputElement).value; }}
                    class="xamix-dialog-input"
                  ></md-outlined-text-field>
                  <md-outlined-text-field
                    label="Texto alternativo (alt)"
                    .value=${this._imageDialogAlt}
                    @input=${(e: Event) => { this._imageDialogAlt = (e.target as HTMLInputElement).value; }}
                    class="xamix-dialog-input"
                  ></md-outlined-text-field>
                  <div style="display:flex;gap:8px;margin-bottom:1rem">
                    <md-outlined-text-field
                      label="Ancho (px)"
                      type="number"
                      .value=${this._imageDialogWidth}
                      @input=${(e: Event) => { this._imageDialogWidth = (e.target as HTMLInputElement).value; }}
                      style="flex:1"
                    ></md-outlined-text-field>
                    <md-outlined-text-field
                      label="Alto (px)"
                      type="number"
                      .value=${this._imageDialogHeight}
                      @input=${(e: Event) => { this._imageDialogHeight = (e.target as HTMLInputElement).value; }}
                      style="flex:1"
                    ></md-outlined-text-field>
                  </div>
                  ${this._imageDialogSrc ? html`<img src=${this._imageDialogSrc} alt="Vista previa" style="max-width:100%;max-height:200px;margin-bottom:1rem;display:block;border:1px solid #ccc;border-radius:4px;">` : ''}
                  <div class="xamix-dialog-actions">
                    <md-text-button type="button" @click=${this._cancelImageDialog}>Cancelar</md-text-button>
                    <md-filled-button type="button" @click=${this._confirmImageDialog}>Insertar</md-filled-button>
                  </div>
                </div>
              </div>`
          : ''}

        ${this._showXamixDialog
          ? html`
              <div
                class="xamix-dialog-backdrop"
                @click=${this._cancelXamix}
              >
                <div
                  class="xamix-dialog"
                  @click=${(e: Event) => e.stopPropagation()}
                >
                  <h3 class="xamix-dialog-title">${this._editingXamixPos != null ? 'Editar Xamix' : 'Insertar Xamix'}</h3>
                  <p class="xamix-dialog-hint">Escribe la representación en sílabas (ej: na-watl, tla'-tol-a-mos-tli)</p>
                  ${!this._inlineXamix
                    ? html`
                        <p class="xamix-dialog-label">Estilo del bloque:</p>
                        <div class="xamix-dialog-levels">
                          ${[0, 1, 2, 3].map((lvl) =>
                            this._xamixBlockLevel === lvl
                              ? html`<md-filled-tonal-button type="button" @click=${() => (this._xamixBlockLevel = lvl as 0 | 1 | 2 | 3)}>${lvl === 0 ? 'Normal' : `Título ${lvl}`}</md-filled-tonal-button>`
                              : html`<md-outlined-button type="button" @click=${() => (this._xamixBlockLevel = lvl as 0 | 1 | 2 | 3)}>${lvl === 0 ? 'Normal' : `Título ${lvl}`}</md-outlined-button>`
                          )}
                        </div>
                      `
                    : ''}
                  <label class="xamix-dialog-checkbox">
                    <md-checkbox ?checked=${this._xamixBold} @change=${(e: Event) => {
                      const checked = (e.target as unknown as { checked: boolean }).checked;
                      this._xamixBold = checked;
                      if (checked) this._xamixBrush = false;
                    }}></md-checkbox>
                    <span>Negrita (bold)</span>
                  </label>
                  <label class="xamix-dialog-checkbox">
                    <md-checkbox ?checked=${this._xamixBrush} @change=${(e: Event) => {
                      const checked = (e.target as unknown as { checked: boolean }).checked;
                      this._xamixBrush = checked;
                      if (checked) this._xamixBold = false;
                    }}></md-checkbox>
                    <span>Estilo pincel (brush)</span>
                  </label>
                  <p class="xamix-dialog-label">Color:</p>
                  <div class="table-color-swatches" style="margin-bottom:1rem">
                    ${TEXT_COLORS.filter((c) => c.value || true).map((c) => html`
                      <button type="button" class="table-color-swatch ${!c.value ? 'transparent' : ''} ${this._xamixColor === c.value ? 'selected' : ''}" style=${c.value ? `background-color:${c.value}` : ''} title=${c.label} @click=${() => { this._xamixColor = c.value; }}></button>
                    `)}
                    <input type="color" style="width:2rem;height:1.5rem;padding:0;border:1px solid #ccc;border-radius:4px;cursor:pointer" .value=${this._xamixColor && /^#[0-9A-Fa-f]{6}$/.test(this._xamixColor) ? this._xamixColor : '#000000'} @input=${(e: Event) => { this._xamixColor = (e.target as HTMLInputElement).value; }}>
                  </div>
                  <md-outlined-text-field
                    label="Sílabas"
                    placeholder="ej: na-watl"
                    .value=${this._xamixInput}
                    @input=${(e: Event) => (this._xamixInput = (e.target as unknown as { value: string }).value ?? '')}
                    @keydown=${(e: KeyboardEvent) => {
                      if (e.key === 'Enter') this._confirmXamix();
                      if (e.key === 'Escape') this._cancelXamix();
                    }}
                    class="xamix-dialog-input"
                  ></md-outlined-text-field>
                  <div class="xamix-dialog-actions">
                    <md-text-button type="button" @click=${this._cancelXamix}>Cancelar</md-text-button>
                    <md-filled-button type="button" @click=${this._confirmXamix}>${this._editingXamixPos != null ? 'Guardar' : 'Insertar'}</md-filled-button>
                  </div>
                </div>
              </div>
            `
          : ''}
        ${this._showTableDialog
          ? html`
              <div class="table-dialog-backdrop" @click=${this._cancelTable}>
                <div class="table-dialog" @click=${(e: Event) => e.stopPropagation()}>
                  <h3 class="table-dialog-title">Insertar tabla</h3>
                  <div class="table-dialog-row">
                    <label for="table-rows">Filas</label>
                    <md-outlined-text-field id="table-rows" type="number" min="1" max="20" .value=${String(this._tableRows)} @input=${(e: Event) => (this._tableRows = Math.max(1, Math.min(20, parseInt((e.target as HTMLInputElement).value, 10) || 1)))}></md-outlined-text-field>
                  </div>
                  <div class="table-dialog-row">
                    <label for="table-cols">Columnas</label>
                    <md-outlined-text-field id="table-cols" type="number" min="1" max="10" .value=${String(this._tableCols)} @input=${(e: Event) => (this._tableCols = Math.max(1, Math.min(10, parseInt((e.target as HTMLInputElement).value, 10) || 1)))}></md-outlined-text-field>
                  </div>
                  <div class="table-dialog-row">
                    <label for="table-margin">Margen</label>
                    <md-outlined-text-field id="table-margin" type="text" placeholder="ej: 1em, 1em 2em" .value=${this._tableMargin} @input=${(e: Event) => (this._tableMargin = (e.target as HTMLInputElement).value ?? '')}></md-outlined-text-field>
                  </div>
                  <div class="table-dialog-row">
                    <label>Color de fondo</label>
                    <div class="table-color-swatches">
                      ${TABLE_COLORS.map((c) => html`
                        <button
                          type="button"
                          class="table-color-swatch ${!c.value ? 'transparent' : ''} ${this._tableBackgroundColor === c.value ? 'selected' : ''}"
                          style=${c.value ? `background-color: ${c.value}` : ''}
                          title=${c.label}
                          @click=${() => (this._tableBackgroundColor = c.value)}
                        ></button>
                      `)}
                    </div>
                    <div class="table-color-input-wrap">
                      <input
                        type="color"
                        title="Elegir color"
                        .value=${this._tableBackgroundColor && /^#[0-9A-Fa-f]{6}$/.test(this._tableBackgroundColor) ? this._tableBackgroundColor : '#ffffff'}
                        @input=${(e: Event) => (this._tableBackgroundColor = (e.target as HTMLInputElement).value)}
                      >
                    </div>
                  </div>
                  <div class="table-dialog-row">
                    <label>Color del borde</label>
                    <div class="table-color-swatches">
                      ${TABLE_COLORS.map((c) => html`
                        <button
                          type="button"
                          class="table-color-swatch ${!c.value ? 'transparent' : ''} ${this._tableBorderColor === c.value ? 'selected' : ''}"
                          style=${c.value ? `background-color: ${c.value}` : ''}
                          title=${c.label}
                          @click=${() => (this._tableBorderColor = c.value)}
                        ></button>
                      `)}
                    </div>
                    <div class="table-color-input-wrap">
                      <input
                        type="color"
                        title="Elegir color"
                        .value=${this._tableBorderColor && /^#[0-9A-Fa-f]{6}$/.test(this._tableBorderColor) ? this._tableBorderColor : '#cac4d0'}
                        @input=${(e: Event) => (this._tableBorderColor = (e.target as HTMLInputElement).value)}
                      >
                    </div>
                  </div>
                  <div class="table-dialog-actions">
                    <md-text-button type="button" @click=${this._cancelTable}>Cancelar</md-text-button>
                    <md-filled-button type="button" @click=${this._confirmTable}>Insertar</md-filled-button>
                  </div>
                </div>
              </div>
            `
          : ''}
        ${this._showSvgDialog
          ? html`
              <div class="xamix-dialog-backdrop" @click=${this._cancelSvgDialog}>
                <div class="xamix-dialog" @click=${(e: Event) => e.stopPropagation()}>
                  <h3 class="xamix-dialog-title">Insertar SVG</h3>
                  <p class="xamix-dialog-hint">Pega el contenido SVG o selecciona un archivo</p>
                  <md-outlined-text-field
                    label="Contenido SVG"
                    type="textarea"
                    rows="6"
                    .value=${this._svgDialogContent}
                    @input=${(e: Event) => { this._svgDialogContent = (e.target as HTMLInputElement).value; }}
                    class="xamix-dialog-input"
                  ></md-outlined-text-field>
                  <div style="margin-bottom:1rem">
                    <md-outlined-button type="button" @click=${this._handleSvgFileSelect}>Seleccionar archivo SVG</md-outlined-button>
                  </div>
                  <div class="xamix-dialog-actions">
                    <md-text-button type="button" @click=${this._cancelSvgDialog}>Cancelar</md-text-button>
                    <md-filled-button type="button" @click=${this._confirmSvgDialog}>Insertar</md-filled-button>
                  </div>
                </div>
              </div>
            `
          : ''}
        </div>
      </div>
    `;
  }
}
